import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Phone as PhoneIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceDialogOpen, setPerformanceDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentPerformance, setAgentPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAgent, setMenuAgent] = useState(null);
  const [performanceOverviewOpen, setPerformanceOverviewOpen] = useState(false);
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('all'); // 'today', 'all', 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchAgents();
  }, [dateFilter, startDate, endDate]);

  const fetchAgents = async () => {
    try {
      // Build query parameters based on date filter
      const params = new URLSearchParams({ includeStats: 'true' });
      
      if (dateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.append('startDate', today);
        params.append('endDate', today);
      } else if (dateFilter === 'custom' && startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      // For 'all', no date parameters are added
      
      const response = await axios.get(`/api/agents?${params}`);
      console.log('Agents API response:', response.data);
      console.log('First agent qaStats:', response.data.data?.[0]?.qaStats);
      
      // Handle the API response structure {success: true, data: [...]}
      if (response.data.success && response.data.data) {
        // Transform the data to include the stats in the right format
        const transformedAgents = response.data.data.map(agent => ({
          ...agent,
          total_calls: agent.stats?.totalCalls || 0,
          average_score: agent.qaStats?.avgScore !== null && agent.qaStats?.avgScore !== undefined ? Math.round(agent.qaStats.avgScore * 10) / 10 : 'N/A'
        }));
        setAgents(transformedAgents);
      } else {
        setAgents([]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to fetch agents');
      setLoading(false);
    }
  };

  const fetchAgentPerformance = async (agentId) => {
    setPerformanceLoading(true);
    try {
      // Build query parameters based on date filter
      const params = new URLSearchParams();
      
      if (dateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.append('startDate', today);
        params.append('endDate', today);
      } else if (dateFilter === 'custom' && startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      
      const url = params.toString() ? `/api/agents/${agentId}?${params}` : `/api/agents/${agentId}`;
      const response = await axios.get(url);
      if (response.data.success) {
        setAgentPerformance(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching agent performance:', err);
    } finally {
      setPerformanceLoading(false);
    }
  };

  const fetchPerformanceOverview = async () => {
    setOverviewLoading(true);
    try {
      // Build query parameters based on date filter
      const params = new URLSearchParams();
      
      if (dateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.append('startDate', today);
        params.append('endDate', today);
      } else if (dateFilter === 'custom' && startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      
      const url = params.toString() ? `/api/agents/performance-overview?${params}` : '/api/agents/performance-overview';
      const response = await axios.get(url);
      if (response.data.success) {
        setOverviewData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching performance overview:', err);
    } finally {
      setOverviewLoading(false);
    }
  };

  const handleActionClick = (event, agent) => {
    setAnchorEl(event.currentTarget);
    setMenuAgent(agent);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAgent(null);
  };

  const handleViewPerformance = (agent) => {
    setSelectedAgent(agent);
    setPerformanceDialogOpen(true);
    fetchAgentPerformance(agent.id);
    handleMenuClose();
  };



  const handleUpdateStatus = async (agent, newStatus) => {
    try {
      await axios.put(`/api/agents/${agent.id}/status`, { status: newStatus });
      fetchAgents(); // Refresh the data
      handleMenuClose();
    } catch (err) {
      console.error('Error updating agent status:', err);
    }
  };

  const handlePerformanceOverview = () => {
    setPerformanceOverviewOpen(true);
    fetchPerformanceOverview();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'name',
      headerName: 'Name',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 1 }} />
          {params.value}
        </Box>
      ),
    },
    { 
      field: 'vici_id', 
      headerName: 'Agent ID', 
      width: 150,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },

    {
      field: 'performance',
      headerName: 'Performance',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            Calls: {params.row.total_calls || 0}
          </Typography>
          <Typography variant="body2">
            Avg Score: {params.row.average_score || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'last_active',
      headerName: 'Last Active',
      width: 140,
      renderCell: (params) => {
        if (!params.value) return 'Never';
        try {
          const date = new Date(params.value);
          if (isNaN(date.getTime())) return 'Invalid Date';
          
          // Format with proper timezone (South African timezone)
          return date.toLocaleDateString('en-ZA', {
            timeZone: 'Africa/Johannesburg',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        } catch (error) {
          return 'Invalid Date';
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Actions">
            <IconButton 
              size="small"
              onClick={(event) => handleActionClick(event, params.row)}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const renderPerformanceDialog = () => (
    <Dialog
      open={performanceDialogOpen}
      onClose={() => setPerformanceDialogOpen(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Agent Performance - {selectedAgent?.name}
          </Typography>
          <IconButton onClick={() => setPerformanceDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {performanceLoading ? (
          <Typography>Loading performance data...</Typography>
        ) : agentPerformance ? (
          <Grid container spacing={3}>
            {/* Date Filter Info */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                <Typography variant="body2" color="text.secondary">
                  {dateFilter === 'today' && 'Showing today\'s performance data'}
                  {dateFilter === 'all' && 'Showing all time performance data'}
                  {dateFilter === 'custom' && startDate && endDate && 
                    `Showing data from ${startDate} to ${endDate}`}
                  {dateFilter === 'custom' && (!startDate || !endDate) && 
                    'Custom date range selected'}
                </Typography>
              </Paper>
            </Grid>
            {/* Overview Cards */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon color="primary" />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {agentPerformance.performance.totalCalls}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Calls
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AssessmentIcon color="success" />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {agentPerformance.performance.avgQAScore > 0 
                        ? Math.round(agentPerformance.performance.avgQAScore * 10) / 10 
                        : 'N/A'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Avg QA Score
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({agentPerformance.performance.totalScoredCalls} scored calls)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUpIcon color="info" />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {agentPerformance.performance.avgDuration > 0 
                        ? Math.round(agentPerformance.performance.avgDuration) + 's'
                        : 'N/A'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Avg Duration
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon color="secondary" />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {agentPerformance.vici_id || 'N/A'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Agent ID
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* QA Scoring Breakdown */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    QA Scoring Breakdown
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        Simple QA Scores (Calls Menu)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Scored Calls: {agentPerformance.performance.simpleScoresCount}
                      </Typography>
                      {agentPerformance.performance.qaBreakdown?.simple?.length > 0 ? (
                        <Box sx={{ mt: 1 }}>
                          {/* Group scores by value and show counts - Simple QA shows text values */}
                          {Object.entries(
                            agentPerformance.performance.qaBreakdown.simple.reduce((acc, score) => {
                              // For Simple QA, use the score as-is (should be text like "Good", "Average", "Poor")
                              const key = score.score;
                              acc[key] = (acc[key] || 0) + 1;
                              return acc;
                            }, {})
                          ).map(([scoreValue, count], index) => (
                            <Chip
                              key={index}
                              label={`${scoreValue} (${count})`}
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                              color={
                                scoreValue.toLowerCase() === 'good' || scoreValue.toLowerCase() === 'excellent' ? 'success' : 
                                scoreValue.toLowerCase() === 'average' ? 'warning' : 'error'
                              }
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No simple QA scores available
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        Detailed QA Scores (Call Analysis)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Scored Calls: {agentPerformance.performance.detailedScoresCount}
                      </Typography>
                      {agentPerformance.performance.qaBreakdown?.detailed?.length > 0 ? (
                        <Box sx={{ mt: 1 }}>
                          {/* Group scores by actual database value and show counts - Detailed QA shows numeric scores */}
                          {Object.entries(
                            agentPerformance.performance.qaBreakdown.detailed.reduce((acc, score) => {
                              // For Detailed QA, use the raw score from database
                              const key = score.score; // This is the actual database value
                              acc[key] = (acc[key] || 0) + 1;
                              return acc;
                            }, {})
                          ).map(([scoreValue, count], index) => (
                            <Chip
                              key={index}
                              label={`${scoreValue} (${count})`}
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                              color={
                                // Check if it's a numeric score
                                !isNaN(scoreValue) ? (
                                  parseFloat(scoreValue) >= 80 ? 'success' : 
                                  parseFloat(scoreValue) >= 60 ? 'warning' : 'error'
                                ) : (
                                  // For text scores in detailed QA
                                  scoreValue.toLowerCase() === 'good' || scoreValue.toLowerCase() === 'excellent' ? 'success' : 
                                  scoreValue.toLowerCase() === 'average' ? 'warning' : 'error'
                                )
                              }
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No detailed QA scores available
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Call Distribution */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Call Distribution by Status
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {agentPerformance.performance.callDistribution?.map((dist, index) => (
                      <Chip
                        key={index}
                        label={`${dist.status}: ${dist.count}`}
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Typography>No performance data available</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPerformanceDialogOpen(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderPerformanceOverviewDialog = () => (
    <Dialog
      open={performanceOverviewOpen}
      onClose={() => setPerformanceOverviewOpen(false)}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Agent Performance Overview
          </Typography>
          <IconButton onClick={() => setPerformanceOverviewOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {overviewLoading ? (
          <Typography>Loading performance overview...</Typography>
        ) : overviewData ? (
          <Box>
            {/* Date Filter Info */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
              <Typography variant="body2" color="text.secondary">
                {dateFilter === 'today' && 'Showing today\'s performance data for all agents'}
                {dateFilter === 'all' && 'Showing all time performance data for all agents'}
                {dateFilter === 'custom' && startDate && endDate && 
                  `Showing agent performance data from ${startDate} to ${endDate}`}
                {dateFilter === 'custom' && (!startDate || !endDate) && 
                  'Custom date range selected for agent performance'}
              </Typography>
            </Paper>
            
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={2}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {overviewData.summary.totalAgents}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Agents
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color="success" />
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {overviewData.summary.activeAgents}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Active Agents
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon color="info" />
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {overviewData.summary.totalCalls}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Calls
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AssessmentIcon color="success" />
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {overviewData.summary.avgQAScore 
                          ? overviewData.summary.avgQAScore 
                          : 'N/A'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Avg QA Score
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

                             <Grid item xs={12} md={2}>
                 <Card>
                   <CardContent>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                       <AssessmentIcon color="warning" />
                       <Typography variant="h6" sx={{ ml: 1 }}>
                         {overviewData.summary.agentsNeedingTraining}
                       </Typography>
                     </Box>
                     <Typography variant="body2" color="text.secondary">
                       Need Training
                     </Typography>
                     <Typography variant="caption" color="text.secondary">
                       ({overviewData.summary.trainingRate}% of agents)
                     </Typography>
                   </CardContent>
                 </Card>
               </Grid>

               <Grid item xs={12} md={2}>
                 <Card>
                   <CardContent>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                       <AssessmentIcon color="error" />
                       <Typography variant="h6" sx={{ ml: 1 }}>
                         {overviewData.summary.highPriorityTraining}
                       </Typography>
                     </Box>
                     <Typography variant="body2" color="text.secondary">
                       High Priority
                     </Typography>
                     <Typography variant="caption" color="text.secondary">
                       Urgent training needed
                     </Typography>
                   </CardContent>
                 </Card>
               </Grid>
            </Grid>

            {/* Training Alert */}
            {overviewData.summary.agentsNeedingTraining > 0 && (
              <Box sx={{ mb: 3 }}>
                <Card sx={{ bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
                  <CardContent>
                    <Typography variant="h6" color="warning.main" gutterBottom>
                      Training Recommendations
                    </Typography>
                    <Typography variant="body2">
                      {overviewData.summary.agentsNeedingTraining} agents require training intervention.
                      {overviewData.summary.highPriorityTraining > 0 && (
                        <> <strong>{overviewData.summary.highPriorityTraining} agents</strong> need immediate attention.</>
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Agents Performance Table */}
            <Paper sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={overviewData.agents}
                columns={[
                  { field: 'id', headerName: 'ID', width: 70 },
                  {
                    field: 'name',
                    headerName: 'Agent Name',
                    width: 150,
                    renderCell: (params) => (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1 }} />
                        {params.value}
                      </Box>
                    ),
                  },
                  { 
                    field: 'vici_id', 
                    headerName: 'Agent ID', 
                    width: 100,
                    renderCell: (params) => params.value || 'N/A'
                  },
                  {
                    field: 'status',
                    headerName: 'Status',
                    width: 100,
                    renderCell: (params) => (
                      <Chip
                        label={params.value}
                        color={getStatusColor(params.value)}
                        size="small"
                      />
                    ),
                  },
                  {
                    field: 'performance.priority',
                    headerName: 'Training Priority',
                    width: 130,
                    renderCell: (params) => {
                      const priority = params.row.performance?.priority;
                      return (
                        <Chip
                          label={priority?.toUpperCase() || 'LOW'}
                          size="small"
                          color={
                            priority === 'high' ? 'error' :
                            priority === 'medium' ? 'warning' : 'success'
                          }
                        />
                      );
                    },
                  },
                  {
                    field: 'performance.totalCalls',
                    headerName: 'Total Calls',
                    width: 100,
                    valueGetter: (params) => params.row.performance?.totalCalls || 0,
                  },
                  {
                    field: 'performance.avgQAScore',
                    headerName: 'Avg QA Score',
                    width: 120,
                    renderCell: (params) => {
                      const score = params.row.performance?.avgQAScore;
                      return (
                        <Chip
                          label={score ? score : 'N/A'}
                          size="small"
                          color={
                            !score ? 'default' :
                            score >= 80 ? 'success' : 
                            score >= 70 ? 'warning' : 'error'
                          }
                        />
                      );
                    },
                  },
                  {
                    field: 'performance.completionRate',
                    headerName: 'Completion Rate',
                    width: 130,
                    renderCell: (params) => {
                      const rate = params.row.performance?.completionRate;
                      return (
                        <Box>
                          <Typography variant="body2">
                            {rate ? `${rate}%` : 'N/A'}
                          </Typography>
                          {rate && rate < 50 && (
                            <Typography variant="caption" color="error">
                              Low
                            </Typography>
                          )}
                        </Box>
                      );
                    },
                  },
                  {
                    field: 'performance.trainingReasons',
                    headerName: 'Training Reasons',
                    width: 350,
                    renderCell: (params) => {
                      const reasons = params.row.performance?.trainingReasons || [];
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                          {reasons.length > 0 ? (
                            reasons.slice(0, 3).map((reason, index) => (
                              <Chip
                                key={index}
                                label={reason}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                                color="warning"
                              />
                            ))
                          ) : (
                            <Chip label="No issues" size="small" color="success" />
                          )}
                          {reasons.length > 3 && (
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                              +{reasons.length - 3} more
                            </Typography>
                          )}
                        </Box>
                      );
                    },
                  },
                  {
                    field: 'qaTypes',
                    headerName: 'QA Types',
                    width: 100,
                    renderCell: (params) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                        {params.row.performance?.hasSimpleScores && (
                          <Chip label="Simple" size="small" sx={{ fontSize: '0.7rem', height: '18px' }} />
                        )}
                        {params.row.performance?.hasDetailedScores && (
                          <Chip label="Detail" size="small" sx={{ fontSize: '0.7rem', height: '18px' }} />
                        )}
                      </Box>
                    ),
                  },
                ]}
                pageSize={15}
                rowsPerPageOptions={[15]}
                disableSelectionOnClick
              />
            </Paper>
          </Box>
        ) : (
          <Typography>No performance data available</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPerformanceOverviewOpen(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Agents
        </Typography>
        <Button
          variant="contained"
          startIcon={<TrendingUpIcon />}
          onClick={handlePerformanceOverview}
        >
          Performance Overview
        </Button>
      </Box>

      {/* Date Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Filter</InputLabel>
              <Select
                value={dateFilter}
                label="Date Filter"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="today">Today Only</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="custom">Custom Date Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {dateFilter === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              {dateFilter === 'today' && 'Showing today\'s performance data'}
              {dateFilter === 'all' && 'Showing all time performance data'}
              {dateFilter === 'custom' && startDate && endDate && 
                `Showing data from ${startDate} to ${endDate}`}
              {dateFilter === 'custom' && (!startDate || !endDate) && 
                'Please select date range'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={agents}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          checkboxSelection
          disableSelectionOnClick
          loading={loading}
          error={error}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewPerformance(menuAgent)}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Performance
        </MenuItem>
        <MenuItem onClick={() => handleUpdateStatus(menuAgent, 'active')}>
          Set Active
        </MenuItem>
        <MenuItem onClick={() => handleUpdateStatus(menuAgent, 'inactive')}>
          Set Inactive
        </MenuItem>
        <MenuItem onClick={() => handleUpdateStatus(menuAgent, 'suspended')}>
          Set Suspended
        </MenuItem>
      </Menu>

      {/* Performance Dialog */}
      {renderPerformanceDialog()}

      {/* Performance Overview Dialog */}
      {renderPerformanceOverviewDialog()}
    </Box>
  );
};

export default Agents; 